<template>
  <div class="primary-contact-form">
    <div class="card-header">
      <h5 class="card-title">
        <i class="fas fa-address-book text-indigo-600 mr-2" />
        Primary & Secondary Contacts
      </h5>
    </div>

    <div
      v-if="loading"
      class="card-body">
      <div class="flex items-center justify-center py-8">
        <div class="text-center">
          <i class="fas fa-spinner fa-spin text-3xl text-indigo-600 mb-2" />
          <p class="text-sm text-gray-500">
            Loading contacts...
          </p>
        </div>
      </div>
    </div>

    <div
      v-else
      class="card-body">
      <!-- View Mode -->
      <div v-if="!isEditing">
        <div
          v-if="hasContactInfo && primaryContact"
          class="space-y-3">
          <!-- Primary Contact Card -->
          <div class="contact-card">
            <div class="contact-card-header">
              <span class="contact-card-title">
                <i class="fas fa-user-tie mr-1.5" />
                Primary Contact
              </span>
            </div>
            <div class="contact-card-body">
              <div
                v-if="primaryContact.name"
                class="contact-info-item">
                <i class="fas fa-user contact-icon" />
                <span class="contact-value">{{ primaryContact.name }}</span>
              </div>
              <div
                v-if="primaryContact.email"
                class="contact-info-item">
                <i class="fas fa-envelope contact-icon" />
                <a
                  :href="`mailto:${primaryContact.email}`"
                  class="contact-link">
                  {{ primaryContact.email }}
                </a>
              </div>
              <div
                v-if="primaryContact.phone"
                class="contact-info-item">
                <i class="fas fa-phone contact-icon" />
                <a
                  :href="`tel:${primaryContact.phone}`"
                  class="contact-link">
                  {{ primaryContact.phone }}
                </a>
              </div>
            </div>
          </div>
        </div>
        <div
          v-else
          class="empty-state">
          <i class="fas fa-user-slash empty-state-icon" />
          <p class="empty-state-text">
            No primary contact information available
          </p>
        </div>

        <!-- Secondary Contact View -->
        <div
          v-if="hasSecondaryContactInfo"
          class="contact-card">
          <div class="contact-card-header">
            <span class="contact-card-title">
              <i class="fas fa-user mr-1.5" />
              Secondary Contact
            </span>
          </div>
          <div class="contact-card-body">
            <div
              v-if="primaryContact.secondaryContactName"
              class="contact-info-item">
              <i class="fas fa-user contact-icon" />
              <span class="contact-value">{{ primaryContact.secondaryContactName }}</span>
            </div>
            <div
              v-if="primaryContact.secondaryContactEmail"
              class="contact-info-item">
              <i class="fas fa-envelope contact-icon" />
              <a
                :href="`mailto:${primaryContact.secondaryContactEmail}`"
                class="contact-link">
                {{ primaryContact.secondaryContactEmail }}
              </a>
            </div>
            <div
              v-if="primaryContact.secondaryContactPhone"
              class="contact-info-item">
              <i class="fas fa-phone contact-icon" />
              <a
                :href="`tel:${primaryContact.secondaryContactPhone}`"
                class="contact-link">
                {{ primaryContact.secondaryContactPhone }}
              </a>
            </div>
          </div>
        </div>

        <div class="mt-4">
          <button
            class="action-button action-button-primary"
            @click="startEditing">
            <i class="fas fa-edit mr-1.5" />
            {{ hasContactInfo ? 'Edit' : 'Add Contacts' }}
          </button>
        </div>
      </div>

      <!-- Edit Mode -->
      <form
        v-else
        class="space-y-4"
        @submit.prevent="saveContact">
        <div class="form-section">
          <h6 class="form-section-title">
            <i class="fas fa-user-tie mr-1.5" />
            Primary Contact
          </h6>
          <div class="form-group">
            <label class="form-label">
              Name <span class="text-red-500">*</span>
            </label>
            <input
              v-model="form.name"
              type="text"
              class="form-input"
              :class="{ 'form-input-error': errors.name }"
              placeholder="Contact name">
            <p
              v-if="errors.name"
              class="form-error">
              {{ errors.name }}
            </p>
          </div>

          <div class="form-group">
            <label class="form-label">Email</label>
            <input
              v-model="form.email"
              type="email"
              class="form-input"
              :class="{ 'form-input-error': errors.email }"
              placeholder="contact@example.com">
            <p
              v-if="errors.email"
              class="form-error">
              {{ errors.email }}
            </p>
          </div>

          <div class="form-group">
            <label class="form-label">Phone</label>
            <input
              v-model="form.phone"
              type="tel"
              class="form-input"
              :class="{ 'form-input-error': errors.phone }"
              placeholder="+1 (555) 123-4567">
            <p
              v-if="errors.phone"
              class="form-error">
              {{ errors.phone }}
            </p>
          </div>
        </div>

        <!-- Secondary Contact Edit -->
        <div class="form-section">
          <h6 class="form-section-title">
            <i class="fas fa-user mr-1.5" />
            Secondary Contact
          </h6>

          <div class="form-group">
            <label class="form-label">Name</label>
            <input
              v-model="form.secondary_contact_name"
              type="text"
              class="form-input"
              placeholder="Secondary contact name">
          </div>

          <div class="form-group">
            <label class="form-label">Email</label>
            <input
              v-model="form.secondary_contact_email"
              type="email"
              class="form-input"
              :class="{ 'form-input-error': errors.secondary_contact_email }"
              placeholder="secondary@example.com">
            <p
              v-if="errors.secondary_contact_email"
              class="form-error">
              {{ errors.secondary_contact_email }}
            </p>
          </div>

          <div class="form-group">
            <label class="form-label">Phone</label>
            <input
              v-model="form.secondary_contact_phone"
              type="tel"
              class="form-input"
              :class="{ 'form-input-error': errors.secondary_contact_phone }"
              placeholder="+1 (555) 123-4567">
            <p
              v-if="errors.secondary_contact_phone"
              class="form-error">
              {{ errors.secondary_contact_phone }}
            </p>
          </div>
        </div>

        <div class="form-actions">
          <div class="flex gap-2">
            <button
              type="submit"
              :disabled="saving"
              class="action-button action-button-success">
              <i
                v-if="saving"
                class="fas fa-spinner fa-spin mr-1.5" />
              <i
                v-else
                class="fas fa-save mr-1.5" />
              Save
            </button>
            <button
              type="button"
              :disabled="saving"
              class="action-button action-button-secondary"
              @click="cancelEditing">
              Cancel
            </button>
          </div>
          <button
            v-if="hasContactInfo"
            type="button"
            :disabled="saving"
            class="action-button action-button-danger"
            @click="deleteContact">
            <i
              v-if="deleting"
              class="fas fa-spinner fa-spin mr-1.5" />
            <i
              v-else
              class="fas fa-trash mr-1.5" />
            Delete
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import mdtoast from '@dmuy/toast'
import axios from '../init/axios'

const props = defineProps({
  organizationId: {
    type: String,
    required: true
  }
})

// State
const loading = ref(true)
const saving = ref(false)
const deleting = ref(false)
const isEditing = ref(false)
const primaryContact = ref({
  name: '',
  email: '',
  phone: '',
  secondary_contact_name: '',
  secondary_contact_email: '',
  secondary_contact_phone: ''
})
const form = ref({
  name: '',
  email: '',
  phone: '',
  secondary_contact_name: '',
  secondary_contact_email: '',
  secondary_contact_phone: ''
})
const errors = ref({})

// Computed
const hasContactInfo = computed(() => {
  if (!primaryContact.value) return false
  return !!(primaryContact.value.name || primaryContact.value.email || primaryContact.value.phone)
})

const hasSecondaryContactInfo = computed(() => {
  if (!primaryContact.value) return false
  return !!(primaryContact.value.secondaryContactName ||
           primaryContact.value.secondaryContactEmail ||
           primaryContact.value.secondaryContactPhone)
})

// Methods
const fetchPrimaryContact = async() => {
  try {
    loading.value = true
    const response = await axios.get(`/ajax/organizations/${props.organizationId}/primary_contact`)
    // eslint-disable-next-line no-console
    console.log('Fetch response:', response.data)
    if (response.data.success) {
      // Handle both cases: when primaryContact exists and when it's null

      primaryContact.value = response.data.primaryContact || {
        name: '',
        email: '',
        phone: '',
        secondaryContactName: '',
        secondaryContactEmail: '',
        secondaryContactPhone: ''
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching primary contact:', error)
    mdtoast.error('Failed to load primary contact', { duration: 3000 })
  } finally {
    loading.value = false
  }
}

const startEditing = () => {
  form.value = {
    name: primaryContact.value?.name || '',
    email: primaryContact.value?.email || '',
    phone: primaryContact.value?.phone || '',
    secondary_contact_name: primaryContact.value?.secondaryContactName || '',
    secondary_contact_email: primaryContact.value?.secondaryContactEmail || '',
    secondary_contact_phone: primaryContact.value?.secondaryContactPhone || ''
  }
  errors.value = {}
  isEditing.value = true
}

const cancelEditing = () => {
  isEditing.value = false
  form.value = {
    name: '',
    email: '',
    phone: '',
    secondaryContactName: '',
    secondaryContactEmail: '',
    secondaryContactPhone: ''
  }
  errors.value = {}
}

const isValidEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return re.test(email)
}

const isValidPhone = (phone) => {
  const re = /^[\d\s\-()+]+$/
  return re.test(phone)
}

const validateForm = () => {
  errors.value = {}
  let isValid = true

  if (!form.value.name?.trim()) {
    errors.value.name = 'Name is required'
    isValid = false
  }

  if (form.value.email && !isValidEmail(form.value.email)) {
    errors.value.email = 'Invalid email format'
    isValid = false
  }

  if (form.value.phone && !isValidPhone(form.value.phone)) {
    errors.value.phone = 'Invalid phone format'
    isValid = false
  }

  if (form.value.secondaryContactEmail && !isValidEmail(form.value.secondaryContactEmail)) {
    errors.value.secondaryContactEmail = 'Invalid email format'
    isValid = false
  }

  if (form.value.secondaryContactPhone && !isValidPhone(form.value.secondaryContactPhone)) {
    errors.value.secondaryContactPhone = 'Invalid phone format'
    isValid = false
  }

  return isValid
}

const saveContact = async() => {
  if (!validateForm()) return

  try {
    saving.value = true
    const response = await axios.patch(
      `/ajax/organizations/${props.organizationId}/primary_contact`,
      { primaryContact: form.value }
    )

    if (response.data.success) {
      // eslint-disable-next-line no-console
      console.log('Save response:', response.data)
      primaryContact.value = response.data.primaryContact || form.value
      isEditing.value = false
      mdtoast.success('Primary contact saved successfully', { duration: 3000 })
    } else {
      mdtoast.error(response.data.error || 'Failed to save primary contact', { duration: 3000 })
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error saving primary contact:', error)
    if (error.response?.data?.error) {
      mdtoast.error(error.response.data.error, { duration: 3000 })
    } else {
      mdtoast.error('Failed to save primary contact', { duration: 3000 })
    }
  } finally {
    saving.value = false
  }
}

const deleteContact = async() => {
  // eslint-disable-next-line no-alert, no-restricted-globals
  if (!confirm('Are you sure you want to delete the primary contact?')) return

  try {
    deleting.value = true
    const response = await axios.delete(`/ajax/organizations/${props.organizationId}/primary_contact`)

    if (response.data.success) {
      primaryContact.value = {
        name: '',
        email: '',
        phone: '',
        secondaryContactName: '',
        secondaryContactEmail: '',
        secondaryContactPhone: ''
      }
      isEditing.value = false
      mdtoast.success('Primary contact deleted successfully', { duration: 3000 })
    } else {
      mdtoast.error(response.data.error || 'Failed to delete primary contact', { duration: 3000 })
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error deleting primary contact:', error)
    mdtoast.error('Failed to delete primary contact', { duration: 3000 })
  } finally {
    deleting.value = false
  }
}

// Lifecycle
onMounted(() => {
  fetchPrimaryContact()
})
</script>

<style scoped>
.primary-contact-form {
  @apply bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden;
}

.card-header {
  @apply px-5 py-4 bg-gray-50 border-b border-gray-200;
}

.card-title {
  @apply text-base font-semibold text-gray-900 flex items-center;
}

.card-body {
  @apply p-5;
}

.contact-card {
  @apply bg-gray-50 rounded-lg overflow-hidden;
}

.contact-card-header {
  @apply px-4 py-2 bg-gray-100 border-b border-gray-200;
}

.contact-card-title {
  @apply text-sm font-medium text-gray-700 flex items-center;
}

.contact-card-body {
  @apply p-4 space-y-2;
}

.contact-info-item {
  @apply flex items-center gap-3 text-sm;
}

.contact-icon {
  @apply w-4 h-4 text-gray-400 flex-shrink-0;
}

.contact-value {
  @apply text-gray-900;
}

.contact-link {
  @apply text-indigo-600 hover:text-indigo-800 hover:underline transition-colors;
}

.empty-state {
  @apply text-center py-8;
}

.empty-state-icon {
  @apply text-4xl text-gray-300 mb-3;
}

.empty-state-text {
  @apply text-sm text-gray-500;
}

.form-section {
  @apply space-y-3;
}

.form-section-title {
  @apply text-sm font-semibold text-gray-700 mb-3 flex items-center;
}

.form-group {
  @apply space-y-1;
}

.form-label {
  @apply block text-sm font-medium text-gray-700;
}

.form-input {
  @apply w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
         focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
         transition-colors duration-200;
}

.form-input-error {
  @apply border-red-500 focus:ring-red-500 focus:border-red-500;
}

.form-error {
  @apply mt-1 text-xs text-red-600;
}

.form-actions {
  @apply flex items-center justify-between mt-6 pt-4 border-t border-gray-200;
}

.action-button {
  @apply inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg
         transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2
         disabled:opacity-50 disabled:cursor-not-allowed;
}

.action-button-primary {
  @apply bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-500;
}

.action-button-success {
  @apply bg-green-600 text-white hover:bg-green-700 focus:ring-green-500;
}

.action-button-secondary {
  @apply bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-500;
}

.action-button-danger {
  @apply bg-red-600 text-white hover:bg-red-700 focus:ring-red-500;
}
</style>
